// PD-16573 — Form Submissions inbox filters (docs/claude/forms/01-design.md §7). The ids
// are read by crm-api's form-submission-list-query.ts, which accepts every value shape the
// list framework sends.

import {
	FORM_SUBMISSION_SPAM_LABELS,
	FORM_SUBMISSION_STATUS_LABELS,
} from '@supply-mover-2-0/shared';
import {
	createDateRangeFilter,
	createKeywordFilter,
	createRadioFilter,
	createStatusFilter,
} from './factories';
import type { FilterConfig, FilterRegistry } from './types';

const LIST_KEYS = ['form_submissions'];

/** A multi-select whose choices come from the inbox's own endpoint. */
function optionsFilter(
	label: string,
	placeholder: string,
	apiEndpoint: string,
	colorScheme: FilterConfig['colorScheme'],
): FilterConfig {
	return {
		type: 'multi-select',
		label,
		placeholder,
		colorScheme,
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		apiEndpoint,
		queryParamName: '',
		multiple: true,
		queryMapping: { objectTable: 'form_submission', joinType: 'direct' },
		format: 'array',
		valueExtractor: { property: 'id' },
		listKeys: LIST_KEYS,
	};
}

export const formSubmissionFilters: FilterRegistry = {
	formSubmissionKeywords: {
		...createKeywordFilter({
			label: 'Keywords',
			placeholder: 'Name, email, phone, company...',
			objectTable: 'form_submission',
			listKeys: LIST_KEYS,
			fulltextFields: [
				'submitterName',
				'submitterEmail',
				'submitterPhone',
				'submitterCompany',
			],
		}),
		queryParamName: 'searchTerm',
	},

	formSubmissionForm: optionsFilter(
		'Form',
		'Search forms...',
		'/form-submissions/filter-options/forms',
		'purple',
	),

	formSubmissionStorefront: optionsFilter(
		'Storefront',
		'Search storefronts...',
		'/form-submissions/filter-options/storefronts',
		'blue',
	),

	formSubmissionStatus: createStatusFilter({
		label: 'Status',
		options: Object.entries(FORM_SUBMISSION_STATUS_LABELS).map(
			([id, label]) => ({ id, label }),
		),
		objectTable: 'form_submission',
		fieldName: 'status',
		listKeys: LIST_KEYS,
		colorScheme: 'green',
	}),

	formSubmissionSpamState: createStatusFilter({
		label: 'Spam check',
		options: Object.entries(FORM_SUBMISSION_SPAM_LABELS).map(([id, label]) => ({
			id,
			label,
		})),
		objectTable: 'form_submission',
		fieldName: 'spamState',
		listKeys: LIST_KEYS,
		colorScheme: 'red',
	}),

	formSubmissionAssignedToMe: createRadioFilter({
		label: 'Assigned',
		options: [
			{ id: 'All', label: 'Anyone' },
			{ id: 'Yes', label: 'Assigned to me' },
		],
		defaultValue: { id: 'All', label: 'Anyone' },
		objectTable: 'form_submission',
		fieldName: 'fkUserIdAssignee',
		listKeys: LIST_KEYS,
		colorScheme: 'blue',
	}),

	formSubmissionFailedActions: createRadioFilter({
		label: 'Actions',
		options: [
			{ id: 'All', label: 'All' },
			{ id: 'Yes', label: 'Has a failed action' },
		],
		defaultValue: { id: 'All', label: 'All' },
		objectTable: 'form_submission',
		fieldName: 'failedActions',
		listKeys: LIST_KEYS,
		colorScheme: 'orange',
	}),

	formSubmissionMatched: createRadioFilter({
		label: 'Matched',
		options: [
			{ id: 'All', label: 'All' },
			{ id: 'confident', label: 'Already on file' },
			{ id: 'possible', label: 'Possible match' },
			{ id: 'none', label: 'No match' },
		],
		defaultValue: { id: 'All', label: 'All' },
		objectTable: 'form_submission',
		fieldName: 'matches',
		listKeys: LIST_KEYS,
		colorScheme: 'yellow',
	}),

	formSubmissionReceived: createDateRangeFilter({
		label: 'Received',
		objectTable: 'form_submission',
		dateField: 'createdDate',
		listKeys: LIST_KEYS,
	}),
};
